<template>
    <div class="pb-20">
        <div class="container relative">
            <SectionIntro
                :title="$t('products.products')"
                :subtitle="$t('products.browse_favourites_subtitle')"
                class="mt-14 lg:mt-20"
                xl
            />
            <div class="grid grid-cols-12 gap-y-8 lg:gap-y-12 lg:gap-x-4 mt-8">
                <div class="col-span-12 lg:col-span-4">
                    <div class="relative h-full">
                        <nuxt-link :to="localePath('pictures')" class="flex flex-col group h-full">
                            <div
                                class="rounded-md overflow-hidden mb-4 aspect-w-4 aspect-h-3 lg:aspect-w-1 lg:aspect-h-1 shadow-lg"
                            >
                                <app-image
                                    class="font-Inter-Bold mb-2 lg:mb-3 text-[1.25rem] object-cover group-hover:scale-[1.04] transition"
                                    :src="
                                        require('~/assets/images/dev/product_category_previews/product_category_preview_image-5.webp')
                                    "
                                    :alt="$t('products.engraved_products')"
                                    preset="tile"
                                    eager
                                />
                            </div>
                            <div class="flex flex-col justify-between h-full">
                                <div>
                                    <h2 class="font-Inter-Bold mb-2 lg:mb-3 text-[1.25rem]">{{ $t('products.index.reprints_title') }}</h2>
                                    <h3 class="text-sm lg:text-base font-Inter-Regular mb-3 opacity-70 leading-5">
                                        {{ $t('products.index.reprints_description') }}
                                    </h3>
                                </div>
                                <span class="flex items-center font-Inter-Bold"
                                    >{{ $t('products.details') }}
                                    <i
                                        class="ri-arrow-right-s-line leading-none text-xl ml-1 transition group-hover:translate-x-1"
                                    ></i
                                ></span>
                            </div>
                        </nuxt-link>
                    </div>
                </div>
                <div class="col-span-12 lg:col-span-4">
                    <EditorPreviewCard
                        eager
                        :title="$t('products.star_map_editor')"
                        :image="
                            require('~/assets/images/dev/product_category_previews/product_category_preview_image-6-b.webp')
                        "
                        slug="editors-star-map"
                        :subtitle="$t('products.index.star_map_editor_subtitle')"
                    />
                </div>
                <div class="col-span-12 lg:col-span-4">
                    <EditorPreviewCard
                        eager
                        :title="$t('products.map_editor')"
                        :image="
                            require('~/assets/images/dev/product_category_previews/product_category_preview_image-7.webp')
                        "
                        slug="editors-map"
                        :subtitle="$t('products.index.map_editor_subtitle')"
                    />
                </div>
                <div class="col-span-12">
                    <div class="relative h-full">
                        <nuxt-link :to="localePath('/termekek/whispers-of-wood')" class="flex flex-col group h-full">
                            <div class="grid grid-cols-2 gap-y-4 lg:gap-y-0 lg:gap-x-8">
                                <div class="col-span-2 lg:col-span-1">
                                    <div class="rounded-md overflow-hidden aspect-w-16 aspect-h-11">
                                        <app-image
                                            class="object-cover group-hover:scale-[1.04] transition transition--slow"
                                            :src="
                                                require('~/assets/images/whispers-of-wood/whispers_of_wood-hero-2.webp')
                                            "
                                            :alt="$t('products.engraved_products')"
                                            preset="tile"
                                            eager
                                        />
                                    </div>
                                </div>
                                <div class="col-span-2 lg:col-span-1">
                                    <div class="flex flex-col h-full justify-center">
                                        <h2 class="font-Inter-Bold mb-2 lg:mb-3 text-[1.25rem]">
                                            {{ $t('products.engraved_products') }}
                                        </h2>
                                        <h3 class="text-sm lg:text-base font-Inter-Regular mb-3 opacity-70 leading-5">
                                            {{ $t('products.index.engraved_description') }}
                                        </h3>
                                        <span class="flex items-center font-Inter-Bold"
                                            >{{ $t('products.details') }}
                                            <i
                                                class="ri-arrow-right-s-line leading-none text-xl ml-1 transition group-hover:translate-x-1"
                                            ></i
                                        ></span>
                                    </div>
                                </div>
                            </div>
                        </nuxt-link>
                    </div>
                </div>
            </div>
            <SectionIntro
                :title="$t('menu.all_products')"
                :subtitle="$t('products.index.all_products_subtitle')"
                class="mt-14 lg:mt-20"
                xl
            />
            <button
                v-if="$device.isMobile"
                class="whitespace-nowrap flex items-center justify-center w-full lg:w-auto font-Inter-Bold bg-white dark:bg-grey-800 border border-black py-1 px-5 rounded-full transition text-black dark:text-white"
                @click="showFilters()"
            >
                <i class="text-xl mr-2" :class="isFiltersVisible ? 'ri-close-line' : 'ri-filter-3-line'"></i
                ><span>{{ $t('products.filters.show') }}</span>
            </button>
            <div
                class="relative flex flex-col md:flex-row md:flex-nowrap w-screen -ml-5 lg:ml-0 lg:w-auto gap-x-4 lg:gap-x-5 items-center mt-10 lg:mt-14 justify-between md:border-b border-black border-opacity-5 nicescroll-medium overflow-x-scroll transition-all lg:overflow-x-visible"
            >
                <div class="category-navigation flex items-center justify-center w-full">
                    <div ref="scrollableContainer" class="flex items-center overflow-x-scroll nicescroll gap-x-6 pb-4">
                        <button
                            v-for="category in categories"
                            :key="`category-${category.id}`"
                            class="whitespace-nowrap font-Inter-Bold text-lg bg-opacity-0 border border-black border-opacity-0 py-1 px-5 transition hover:border-opacity-100 rounded-full"
                            :class="{
                                'text-white border-blue-100 hover:text-white border-opacity-100 bg-opacity-100 bg-blue-100 hover:bg-opacity-100':
                                    isActiveTab(category.id),
                            }"
                            @click="selectCategory(category?.id, $event)"
                        >
                            {{ category.translations[0].name }}
                        </button>
                    </div>
                </div>
            </div>
            <transition name="fade">
                <div
                    v-if="isFiltersVisible || $device.isDesktop"
                    class="grid grid-cols-12 mt-8 lg:gap-x-8 gap-y-4 lg:gap-y-0 pb-6 border-b border-black border-opacity-10 mb-8"
                >
                    <div class="col-span-12 lg:col-span-4">
                        <Input
                            v-model="filters.searchText"
                            :label="$t('products.filters.search_label')"
                            :placeholder="$t('products.filters.search_placeholder')"
                            @input="updateQueries"
                        />
                    </div>
                    <div class="col-span-12 lg:col-span-4">
                        <Input
                            v-model="filters.orientation"
                            :label="$t('products.filters.orientation_label')"
                            :placeholder="$t('products.filters.orientation_placeholder')"
                            has-clear-btn
                            :options="orientation"
                            @input="updateQueries"
                        />
                    </div>
                    <div class="col-span-12 lg:col-span-4">
                        <Input
                            v-model="filters.arrangement"
                            :label="$t('products.filters.sort_label')"
                            :placeholder="$t('products.filters.sort_placeholder')"
                            has-clear-btn
                            :options="arrangement"
                            @input="updateQueries"
                        />
                    </div>
                </div>
            </transition>
            <div v-if="(products && products.length) || processedProducts" class="mt-10">
                <ProductGrid :products="processedProducts || products" :product-count="10" />
            </div>
            <div v-else class="text-center mb-12 lg:mb-0 flex flex-col items-center justify-center h-full mt-24">
                <i class="sad-emoji mb-3" data-not-lazy></i>
                <h4 class="font-Inter-Bold text-xl lg:text-[1.5rem] mb-4 leading-[2rem]" v-html="$t('products.no_results_title')"></h4>
                <p class="lg:w-8/12 text-sm lg:text-base">
                    {{ $t('products.no_results_text') }}
                </p>
            </div>
            <ScrollToTopButton />
        </div>
    </div>
</template>

<script>
import { debounce } from 'vue-debounce';
import ProductGrid from '~/components/Layout/ProductGrid.vue';
import ScrollToTopButton from '@/components/UI/Buttons/ScrollToTopButton.vue';
import EditorPreviewCard from '@/components/Cards/EditorPreviewCard/EditorPreviewCard.vue';
export default {
    name: 'ProductFilter',
    components: {
        EditorPreviewCard,
        ProductGrid,
        ScrollToTopButton,
    },
    async asyncData({ $axios, query }) {
        let products = [];
        let categories = [];
        let highlightedCategories = [];
        let featuredOffers = [];
        let highlightedProducts = [];
        let mainCategory = null;

        const queryParams = {
            search: query.search,
            orientation: query.orientation,
            order: query.order,
        };

        try {
            await Promise.allSettled([
                $axios.$get(`/products`, { params: queryParams }),
                $axios.$get(`/categories`),
                $axios.$get(`/highlighted-categories`),
                $axios.$get('/featured-offers'),
                $axios.$get('/highlighted-products'),
            ])
                .then(
                    ([
                        productRes,
                        categoryRes,
                        highlightedCategoriesRes,
                        featuredOffersRes,
                        highlightedProductsRes,
                    ]) => {
                        if (productRes.status === 'fulfilled' && productRes.value.result === 'success') {
                            products = productRes.value.data;
                        }
                        if (categoryRes.status === 'fulfilled' && categoryRes.value.result === 'success') {
                            categories = categoryRes.value.data;
                            mainCategory = categoryRes.value.data.find((category) => category?.main === 1);
                        }
                        if (
                            highlightedCategoriesRes.status === 'fulfilled' &&
                            highlightedCategoriesRes.value.result === 'success'
                        ) {
                            highlightedCategories = highlightedCategoriesRes.value.data;
                        }
                        if (featuredOffersRes.status === 'fulfilled' && featuredOffersRes.value?.result === 'success') {
                            featuredOffers = featuredOffersRes.value.data;
                        }
                        if (
                            highlightedProductsRes.status === 'fulfilled' &&
                            highlightedProductsRes.value.result === 'success'
                        ) {
                            highlightedProducts = highlightedProductsRes.value.data;
                        }
                    }
                )
                .catch((err) => {
                    console.log(err);
                });
        } catch (err) {
            console.log(err);
        }

        return {
            products,
            categories,
            highlightedCategories,
            featuredOffers,
            highlightedProducts,
            mainCategory,
        };
    },
    data() {
        return {
            categories: null,
            products: null,
            processedProducts: null,
            productCount: 10,
            resultsCount: 200,
            selectedCategory: null,
            highlightedCategories: null,
            isFiltersVisible: false,
            filters: {
                searchText: '',
                brand: null,
                orientation: null,
                arrangement: null,
            },
            selectedBrand: null,
        };
    },
    head() {
        const meta = [
            {
                hid: 'og:title',
                property: 'og:title',
                content: this.$t('footer.gallery'),
            },
            {
                hid: 'title',
                name: 'title',
                content: this.$t('products.meta.posters_title'),
            },
            {
                hid: 'og:description',
                property: 'og:description',
                content: this.$t('products.browse_favourites_subtitle'),
            },
            {
                hid: 'description',
                name: 'description',
                content: this.$t('products.browse_favourites_subtitle'),
            },
        ];
        return {
            title: this.$t('footer.gallery'),
            meta,
        };
    },
    computed: {
        arrangement() {
            return [
                { id: 'SORT_ASC', name: this.$t('products.filters.sort_oldest') },
                { id: 'SORT_DESC', name: this.$t('products.filters.sort_newest') },
            ];
        },
        orientation() {
            return [
                { id: 'vertical', name: this.$t('products.filters.orientation_vertical') },
                { id: 'horizontal', name: this.$t('products.filters.orientation_horizontal') },
            ];
        },
    },
    watch: {
        $route(from, to) {
            this.filterProducts(from.query, from.params);
        },
    },
    beforeMount() {
        this.filters.searchText = this.$route.query.search;

        if (this.$route.query.orientation) {
            this.filters.orientation = this.$route.query.orientation;
        }
        if (this.$route.query.order) {
            this.filters.arrangement = this.$route.query.order;
        }
    },
    methods: {
        loadMoreProducts() {
            if (this.processedProducts.length > this.productCount) {
                this.productCount = this.productCount + 5;
            }
        },
        scrollBasedOnPosition(event) {
            const container = this.$refs.scrollableContainer;
            if (!container) return;

            const containerRect = container.getBoundingClientRect(); // Container's bounding box
            const itemRect = event.target.getBoundingClientRect(); // Clicked item's bounding box

            const itemPosition = itemRect.left - containerRect.left; // Distance from the container's left edge
            const containerWidth = containerRect.width;

            if (itemPosition < containerWidth / 2) {
                container.scrollTo({
                    left: 0,
                    behavior: 'smooth',
                });
            } else {
                container.scrollTo({
                    left: container.scrollWidth,
                    behavior: 'smooth',
                });
            }
        },
        selectCategory(currentCategory, event) {
            this.processedProducts = this.products.filter((product) => {
                return product.category_id === parseInt(currentCategory);
            });
            this.selectedCategory = currentCategory;
            this.scrollBasedOnPosition(event);
        },
        isActiveTab(tabId) {
            return this.selectedCategory === tabId;
        },
        resetProducts() {
            this.processedProducts = this.products;
            this.selectedCategory = null;
        },
        showFilters() {
            this.isFiltersVisible = !this.isFiltersVisible;
        },
        updateQueries: debounce(function () {
            this.$router.push({
                path: this.$route.path,
                query: {
                    search: this.filters.searchText,
                    orientation: this.filters.orientation,
                    order: this.filters.arrangement,
                },
            });
        }, 250),
        filterProducts(query, params) {
            const queryParams = {
                search: query.search,
                orientation: query.orientation,
                order: query.order,
            };

            this.$axios
                .get(`/products`, { params: queryParams })
                .then((response) => {
                    this.processedProducts = response.data.data;
                })
                .catch((err) => {
                    console.log(err);
                });
        },
    },
};
</script>

<style>
.category-navigation {
    @apply relative;
    &::after {
        content: '';
        @apply absolute h-full w-8 block right-0 top-0 pointer-events-none;
        background: linear-gradient(270deg, #fdfdfd 0%, rgba(253, 253, 253, 0) 100%);
    }
    &::before {
        content: '';
        @apply absolute h-full w-8 block -left-4 lg:-left-8 top-0 pointer-events-none rotate-180;
        background: linear-gradient(270deg, #fdfdfd 0%, rgba(253, 253, 253, 0) 100%);
    }
}
.category-link {
    @apply flex items-center;

    &::after {
        content: '';
        @apply w-2 h-2 bg-no-repeat bg-center bg-contain ml-2 block transition-all mt-[0.1rem];
        background-image: url('~/assets/images/icons/chevron-right-white.svg');
    }
}
.category-card {
    img {
        @apply transition-all;
    }

    &::before {
        content: '';
        @apply absolute top-0 left-0 w-3/5 h-full block pointer-events-none z-1;
        background: linear-gradient(90deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0) 100%);
    }

    &:hover {
        .category-link {
            &::after {
                @apply translate-x-1;
            }
        }

        img {
            @apply scale-105;
        }
    }
}
.banner-card {
    .cover-image {
        @apply transition-all;
    }
    &::before {
        content: '';
        @apply absolute top-0 left-0 w-full h-1/2 block pointer-events-none z-1;
        background: linear-gradient(180deg, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0) 100%);
    }
    &:hover {
        .cover-image {
            @apply scale-[103%];
        }
    }
}
.featured-product-cart {
    .cover-image {
        @apply transition-all;
    }
    &:hover {
        .cover-image {
            @apply scale-[103%];
        }
    }
}

.fade-out-r {
    &::before {
        content: '';
        @apply absolute right-0 top-0 w-8 h-full pointer-events-none;
        background: linear-gradient(270deg, #fff 0%, rgba(255, 255, 255, 0) 100%);
    }
}
</style>
